import { useState } from 'react';
import { Label, Input, ItemSelector, SkeletonBase } from '@unified-checkout/design-system';
import { useTranslation } from 'react-i18next';
import { ICreateIntegrationModal } from '@app/dashboard/types/innerLayout/payment_integrations/create-integrations';
import styles from './CreateIntegration.module.scss';

export const CreateIntegration = ({
  paymentTypeOptions = [],
  currencyOptions = [],
  selectedPlatform,
  selectedPaymentType,
  selectedCurrency,
  onPlatformChange,
  onPaymentTypeChange,
  onCurrencyChange,
  processedCallbackValue = '',
  responseCallbackValue = '',
  onProcessedCallbackChange,
  onResponseCallbackChange,
  isOnboarding = false,
  onboardingStatus = '',
}: ICreateIntegrationModal) => {
  const { t } = useTranslation();

  const platformOptions = [
    { label: 'Shopify', value: 'shopify' },
    { label: 'Non-Shopify', value: 'non_shopify' },
  ];

  const [platform, setPlatform] = useState<string>(selectedPlatform || '');
  const [paymentType, setPaymentType] = useState<string | null>(selectedPaymentType || null);
  const [currency, setCurrency] = useState<string | null>(selectedCurrency || null);
  const [processedCallbackError, setProcessedCallbackError] = useState<string>('');
  const [responseCallbackError, setResponseCallbackError] = useState<string>('');

  const validateURL = (url: string): boolean => {
    try {
      new URL(url);
      return true;
    } catch {
      return false;
    }
  };

  const handleProcessedCallbackChange = (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    const value = e.target.value;
    if (value && !validateURL(value)) {
      setProcessedCallbackError(
        t(
          'innerLayout.paymentIntegrationsPage.table.headers.paymentIntegration.errorMessage'
        )
      );
    } else {
      setProcessedCallbackError('');
    }
    onProcessedCallbackChange?.(value);
  };

  const handleResponseCallbackChange = (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    const value = e.target.value;
    if (value && !validateURL(value)) {
      setResponseCallbackError(
        t(
          'innerLayout.paymentIntegrationsPage.table.headers.paymentIntegration.errorMessage'
        )
      );
    } else {
      setResponseCallbackError('');
    }
    onResponseCallbackChange?.(value);
  };

  const handlePlatformChange = (value: string) => {
    setPlatform(value);
    onPlatformChange?.(value);
  };

  const isPaypalType = paymentType?.toLowerCase() === 'paypal';

  const isOnboardingPaypal = isPaypalType && Boolean(isOnboarding) && Boolean(onboardingStatus);

  return (
    <div className={styles['create-integration-modal']}>

      <div className={styles['create-integration-modal__section']}>
        <Label text={t('innerLayout.paymentIntegrationsPage.table.headers.paymentIntegration.channel')} />
        <div className={styles['create-integration-modal__section__radio-group']}>
          {platformOptions.map((opt: any) => (
            <ItemSelector
              key={opt.value}
              id={opt.value}
              type="radio"
              size="md"
              text={opt.label}
              selected={platform === opt.value}
              onClickHandler={(checked) => {
                if (checked) {
                  handlePlatformChange(opt.value);
                }
              }}
            />
          ))}
        </div>

        <Label text={t('innerLayout.paymentIntegrationsPage.table.headers.paymentIntegration.typeOfIntegration')} />
        <div className={styles['create-integration-modal__section__radio-group']}>
          {paymentTypeOptions.length > 0 ? (
            paymentTypeOptions.map((opt: any) => (
              <ItemSelector
                key={opt.value}
                id={opt.value}
                type="radio"
                size="md"
                text={opt.label}
                selected={paymentType === opt.value}
                onClickHandler={(checked) => {
                  if (checked) {
                    setPaymentType(opt.value);
                    setCurrency(null);
                    onPaymentTypeChange?.(opt.value);
                  }
                }}
              />
            ))
          ) : (
            Array.from({ length: 4 }).map((_, index) => (
              <div
                key={`payment-type-skeleton-${index}`}
                className={styles['create-integration-modal__section__radio-group__skeleton']}
              >
                <SkeletonBase height="3rem" />
              </div>
            ))
          )}
        </div>

      </div>

      {isOnboardingPaypal && (
        <div className={styles['create-integration-modal__boarding-message']}>{onboardingStatus}</div>
      )}

      {!isOnboardingPaypal && (
        <>
          {currencyOptions.length > 0 && (
            <>
              <Label text={t('innerLayout.paymentIntegrationsPage.table.headers.paymentIntegration.currency')} />
              <div
                className={`${styles['create-integration-modal__section__radio-group']} ${styles['create-integration-modal__section__radio-group--three-columns']}`}
              >
                {currencyOptions.map((opt: any) => (
                  <ItemSelector
                    key={opt.value}
                    id={opt.value}
                    type="radio"
                    size="md"
                    text={opt.label}
                    selected={currency === opt.value}
                    onClickHandler={(checked) => {
                      if (checked) {
                        setCurrency(opt.value);
                        onCurrencyChange?.(opt.value);
                      }
                    }}
                  />
                ))}
              </div>
            </>
          )}

          <div className={styles['section']}>
            <Input
              type="text"
              name="processed-callback-url"
              value={processedCallbackValue}
              label={t('innerLayout.paymentIntegrationsPage.table.headers.paymentIntegration.proccesUrl')}
              error={processedCallbackError}
              status={processedCallbackError ? 'error' : 'default'}
              onChange={handleProcessedCallbackChange}
              placeholder={t('innerLayout.paymentIntegrationsPage.table.headers.paymentIntegration.placeholder')}
            />
          </div>

          <div className={styles['section']}>
            <Input
              type="text"
              name="response-callback-url"
              value={responseCallbackValue}
              label={t('innerLayout.paymentIntegrationsPage.table.headers.paymentIntegration.responseUrl')}
              error={responseCallbackError}
              status={responseCallbackError ? 'error' : 'default'}
              onChange={handleResponseCallbackChange}
              placeholder={t('innerLayout.paymentIntegrationsPage.table.headers.paymentIntegration.placeholder')}
            />
          </div>
        </>
      )}
    </div>
  );
};
